<template lang="pug">
el-form(
  ref="refForm"
  :rules="rules"
  :model="item"
  status-icon
  label-position="top"
).c-user-edit-form
  app-main-content-layout(title='User Detail' back-to='/manage/users')
    template(#actions)
      el-button(type="default" plain).button-dark-outlined.min-w-110
        fai(type="fal" icon="edit").size-18
        span Send Message

      el-button(type="danger" plain @click="removeItem").has-fai.min-w-110
        fai(type="fal" icon="trash").size-18
        span Delete

      el-button(type="primary"  color="#1EBEAF" @click="validateForm" v-loading="loading").has-fai.min-w-110
        fai(type="fal" icon="edit").size-18
        span Save

    template(#col-left)
      .c-content-section
        .row
          .col-md-6
            el-form-item(label="First Name" prop="name")
              el-input(v-model="item.firstname")
          .col-md-6
            el-form-item(label="Last Name" prop="name").mb-0
              el-input(v-model="item.lastname")

        el-form-item
          template(#label)
            | Slug
            el-tooltip(
              effect="customized"
              :content="slugTip"
              placement="top"
              popper-class="max-w-350"
            )
              fai(type="fal" icon="info-circle").size-18.ms-2
          el-input(v-model="item.slug" @input="sanitizeSlug")

        el-form-item
          template(#label)
            | My Why
            el-tooltip(
              effect="customized"
              :content="myWhyTip"
              placement="top"
              popper-class="max-w-350"
            )
              fai(type="fal" icon="info-circle").size-18.ms-2

          el-input(v-model="item.why" type="textarea")

        el-form-item.mb-0
          template(#label)
            | Bio
            el-tooltip(
              effect="customized"
              :content="bioTip"
              placement="top"
              popper-class="max-w-350"
            )
              fai(type="fal" icon="info-circle").size-18.ms-2
          el-input(v-model="item.bio" type="textarea")

      .c-content-section.mt-3
        user-common-sections(:item="item")
    template(#col-right)
      .c-content-section
        el-form-item(label="Account Status")
          VueMultiselect(
              v-model="item.active",
              :multiple="false",
              label="type",
              track-by="value",
              :options="accountStatus.items",
              :searchable="false",
              :close-on-select="true",
              :show-labels="false"
              placeholder="Select"
            )
        // Share User Profile QR Code
        el-form-item(label="Share User Profile")
          .qr-code-section
            qrcode-vue(:value="userUrl" :size="220")
            .link-with-copy.mt-2
              a.primary-color-text(
                :href="userUrl"
                target="_blank"
                rel="noopener noreferrer"
              ) {{ userUrl }}
              el-button(type="primary" size="small" @click.stop="copyUserUrl").ms-2
                span Copy

        el-form-item(label="In-App User Link")
          .link-input-group
            el-input(:value="userDeepLink" readonly size="small")
            el-button(type="primary" size="small" @click="copyUserDeepLink")
              span.ms-1 Copy

        el-form-item(label="Permissions")
          .d-flex
            div Creator
            el-switch.ms-auto(
              v-model="item.is_creator"
              active-color="#13ce66"
              active-value=true
              inactive-value=false)

      .c-content-section.mt-3
        el-form-item(label="Email")
          el-input(v-model="item.email" type="email" disabled)

        el-form-item(label="Phone")
          el-input(
            v-model="item.phone"
            v-mask="'(###) ###-####'"
            type="tel"
            placeholder="(123) 456-7890"
            disabled
          )

        el-form-item(label="Age")
          el-input(
            v-model="item.age"
            disabled
          )

        el-form-item
          template(#label) Created
            span.text-muted.ms-2
          el-input(v-model="item.createdAt" disabled)

        el-form-item(v-if="item.organizations.length")
          template(#label) Organizations
          .organizations-list
            .organization-item(
              v-for="org in item.organizations"
              :key="org.orgId"
              @click="viewOrganization(org.orgId, $event)"
            )
              .row.row-xs.align-items-center
                .col-auto
                  img(:src="org.avatar"
                    v-if="org.avatar").d-block.size-32.obj-f-c.rounded-circle
                  img(src="@/assets/images/default-profile.jpeg"
                    v-if="!org.avatar").d-block.size-32.obj-f-c.rounded-circle
                .col.lh-1 {{ org.name }}
                .col-auto
                  el-button(type="primary" plain size="small" @click.stop="viewOrganization(org.orgId, $event)").view-details-btn
                    span View Details
</template>

<style lang="scss" scoped>
// Color Variables - Match ManageUsers.vue
$color-teal-accent: #01beb0;
$gray-50: #f9fafb;
$gray-100: #f3f4f6;
$gray-200: #e5e7eb;
$gray-300: #d1d5db;
$gray-400: #9ca3af;
$gray-500: #6b7280;
$gray-600: #4b5563;
$gray-700: #374151;
$gray-900: #111827;
$color-purple-text: #4349dd;

// Main Form Container - Match ManageUsers.vue padding
.c-user-edit-form {
  padding: 2rem;
  
  // Add spacing between sections
  :deep(.grid-2-cols) {
    gap: 1.5rem;
  }
  
  :deep(.col-has-child) {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
}

// White Content Sections - Match Activity page
.c-content-section {
  background: white;
  border-radius: 16px;
  border: 1px solid #dfdfdf;
  box-shadow: none;
  padding: 1.5rem;
  overflow: hidden;
}

// Link Input Group Styling
.link-input-group {
  display: flex;
  gap: 8px;
  align-items: center;
}

.link-input-group .el-input {
  flex: 1;
}

// Form Styling - Match Activity page
:deep(.el-form-item) {
  margin-bottom: 1.5rem;

  .el-form-item__label {
    color: $gray-700;
    font-weight: 600;
    font-size: 0.875rem;
    line-height: 1.5;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .el-input__wrapper {
    border-radius: 10px;
    border: 1.5px solid $gray-200;
    background: #F0F0F3;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
    padding: 0 12px;
    height: 40px;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover:not(.is-disabled) {
      border-color: $color-purple-text;
      box-shadow: 0 2px 4px 0 rgba(67, 73, 221, 0.08);
    }

    &.is-focus {
      border-color: $color-purple-text;
      box-shadow: 0 0 0 3px rgba(67, 73, 221, 0.12), 0 2px 4px 0 rgba(67, 73, 221, 0.08);
    }

    &.is-disabled {
      border: 1.5px solid $gray-200;
      background: #F0F0F3;
    }
  }

  .el-input__inner {
    height: 100%;
    font-size: 0.875rem;
    color: $gray-700;
    background: transparent;
    font-weight: 500;

    &::placeholder {
      color: $gray-400;
      font-weight: 400;
    }
  }

  .el-textarea__inner {
    border-radius: 10px;
    border: 1.5px solid $gray-200;
    background: #F0F0F3;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
    padding: 12px;
    color: $gray-700;
    font-weight: 500;
    font-size: 0.875rem;
    line-height: 1.5;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    min-height: 100px;

    &:hover {
      border-color: $color-purple-text;
      box-shadow: 0 2px 4px 0 rgba(67, 73, 221, 0.08);
    }

    &:focus {
      border-color: $color-purple-text;
      box-shadow: 0 0 0 3px rgba(67, 73, 221, 0.12), 0 2px 4px 0 rgba(67, 73, 221, 0.08);
    }

    &::placeholder {
      color: $gray-400;
      font-weight: 400;
    }
  }
}

// Button Styling - Match Activity page
:deep(.el-button) {
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  height: 36px;
  padding: 0 20px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border-width: 1px;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 0.5rem;

  > span {
    display: inline-flex;
    align-items: center;
    line-height: 1;
  }

  .fai,
  svg {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    margin-right: 0.5rem;
  }

  &.min-w-110 {
    min-width: 110px;
  }

  &.el-button--primary {
    background-color: $color-purple-text;
    border-color: $color-purple-text;
    color: white;

    &:hover,
    &:focus {
      background-color: color.adjust($color-purple-text, $lightness: -8%);
      border-color: color.adjust($color-purple-text, $lightness: -8%);
      box-shadow: 0 2px 4px 0 rgba(67, 73, 221, 0.2);
      transform: translateY(-1px);
    }

    &:active {
      background-color: color.adjust($color-purple-text, $lightness: -10%);
      border-color: color.adjust($color-purple-text, $lightness: -10%);
      transform: translateY(0);
      box-shadow: 0 1px 2px 0 rgba(67, 73, 221, 0.2);
    }
  }

  &.el-button--danger {
    &.is-plain {
      background-color: white;
      border-color: #f56c6c;
      color: #f56c6c;

      &:hover {
        background-color: #fef0f0;
        border-color: #f56c6c;
        color: #f56c6c;
        box-shadow: 0 2px 4px 0 rgba(245, 108, 108, 0.2);
        transform: translateY(-1px);
      }

      &:active {
        transform: translateY(0);
      }
    }
  }

  &.el-button--default {
    background-color: white;
    border-color: $gray-200;
    color: $gray-700;

    &:hover {
      background-color: $gray-50;
      border-color: $gray-300;
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.05);
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }
  }

  &.button-green {
    background-color: $color-teal-accent;
    border-color: $color-teal-accent;
    color: white;

    &:hover {
      background-color: color.adjust($color-teal-accent, $lightness: -5%);
      border-color: color.adjust($color-teal-accent, $lightness: -5%);
      box-shadow: 0 2px 4px 0 rgba(1, 190, 176, 0.2);
      transform: translateY(-1px);
    }

    &:active {
      background-color: color.adjust($color-teal-accent, $lightness: -8%);
      border-color: color.adjust($color-teal-accent, $lightness: -8%);
      transform: translateY(0);
      box-shadow: 0 1px 2px 0 rgba(1, 190, 176, 0.2);
    }
  }

  &.button-dark-outlined {
    background-color: white;
    border-color: $gray-700;
    color: $gray-700;

    &:hover {
      background-color: $gray-50;
      border-color: $gray-900;
      color: $gray-900;
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
      transform: translateY(-1px);
    }

    &:active {
      background-color: $gray-100;
      transform: translateY(0);
      box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.1);
    }
  }

  &.el-button--small {
    padding: 0 12px;
    font-size: 0.8125rem;
    height: 32px;
  }
}

// Switch Styling
:deep(.el-switch) {
  height: 24px;
  line-height: 24px;

  .el-switch__core {
    border-radius: 12px;
    height: 24px;
    min-width: 44px;
    border: 2px solid $gray-300;
    background-color: $gray-300;
    transition: all 0.3s;

    .el-switch__action {
      width: 20px;
      height: 20px;
      left: 2px;
      top: 50%;
      transform: translateY(-50%);
      transition: all 0.3s;
    }
  }

  &.is-checked {
    .el-switch__core {
      background-color: #13ce66;
      border-color: #13ce66;

      .el-switch__action {
        left: calc(100% - 22px);
      }
    }
  }
}

// Select/Multiselect Styling - Match Activity page
:deep(.multiselect) {
  .multiselect__tags {
    border-radius: 10px;
    border: 1.5px solid $gray-200;
    background: #F0F0F3;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
    padding: 8px 12px;
    min-height: 40px;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      border-color: $color-purple-text;
      box-shadow: 0 2px 4px 0 rgba(67, 73, 221, 0.08);
    }
  }

  &.multiselect--active .multiselect__tags {
    border-color: $color-purple-text;
    box-shadow: 0 0 0 3px rgba(67, 73, 221, 0.12), 0 2px 4px 0 rgba(67, 73, 221, 0.08);
  }

  .multiselect__single,
  .multiselect__input {
    color: $gray-700;
    font-weight: 500;
    font-size: 0.875rem;
    background: transparent;
    line-height: 24px;
    padding: 0;
    margin: 0;
    display: flex;
    align-items: center;
  }

  .multiselect__placeholder {
    color: $gray-400;
    font-size: 0.875rem;
    font-weight: 400;
  }

  .multiselect__content-wrapper {
    border-radius: 12px;
    border: 1.5px solid $gray-200;
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.08);
    margin-top: 6px;
  }

  .multiselect__option {
    border-radius: 8px;
    padding: 10px 12px;
    margin: 2px 6px;
    font-size: 0.9375rem;
    color: $gray-700;
    font-weight: 500;
    transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);

    &--highlight {
      background-color: rgba(67, 73, 221, 0.08);
      color: $color-purple-text;
      transform: translateX(2px);
    }

    &--selected {
      background-color: rgba(67, 73, 221, 0.12);
      color: $color-purple-text;
      font-weight: 600;
    }
  }
}

// Status Badge Styling - Match ManageUsers.vue
.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.125rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.5;
  white-space: nowrap;
  border: 1px solid;

  &--active {
    background-color: #D1F4F0;
    color: #0D9488;
    border-color: #14B8A6;
  }

  &--archived {
    background-color: $gray-100;
    color: $gray-600;
    border-color: $gray-300;
  }

  &--invited {
    background-color: #E0E7FF;
    color: #4F46E5;
    border-color: #818CF8;
  }
}

// Font utilities - Match ManageUsers.vue
.font-black {
  font-weight: 600;
  color: $gray-900;
}

.font-medium {
  font-weight: 500;
  color: $gray-700;
}

.lh-sm {
  line-height: 1.25;
}

// QR Code Section Styling
.qr-code-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.link-with-copy {
  display: flex;
  align-items: center;
  width: 100%;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.primary-color-text {
  color: $color-purple-text;
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  word-break: break-all;
  transition: color 0.2s ease;
  flex: 1;
  min-width: 0;

  &:hover {
    color: color.adjust($color-purple-text, $lightness: -10%);
    text-decoration: underline;
  }
}

// Organizations List Styling
.organizations-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.organization-item {
  background-color: $gray-50;
  border: 1px solid $gray-200;
  border-radius: 10px;
  padding: 0.75rem;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background-color: color.adjust($color-purple-text, $lightness: 45%);
    border-color: $color-purple-text;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px 0 rgba(67, 73, 221, 0.1);
  }

  &:active {
    transform: translateY(0);
  }
}

.view-details-btn {
  border-color: $color-purple-text !important;
  color: $color-purple-text !important;
  background-color: white !important;
  font-weight: 600;
  
  &:hover {
    background-color: color.adjust($color-purple-text, $lightness: 45%) !important;
    border-color: color.adjust($color-purple-text, $lightness: -5%) !important;
    color: color.adjust($color-purple-text, $lightness: -5%) !important;
  }
}

.row-xs {
  margin-left: -0.25rem;
  margin-right: -0.25rem;

  > [class*="col"] {
    padding-left: 0.25rem;
    padding-right: 0.25rem;
  }
}

.size-32 {
  width: 32px;
  height: 32px;
}

.obj-f-c {
  object-fit: cover;
}

.rounded-circle {
  border-radius: 50%;
}

.lh-1 {
  line-height: 1;
  font-weight: 500;
  color: $gray-900;
  font-size: 0.875rem;
}

// Tooltip Styling
:deep(.el-tooltip__trigger) {
  .size-18 {
    color: $gray-400;
    transition: color 0.2s ease;

    &:hover {
      color: $gray-600;
    }
  }
}

// Element Plus Select Dropdown Styling - Match Activity page
:deep(.el-select-dropdown) {
  border-radius: 12px;
  border: 1px solid $gray-200;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.08);
  padding: 6px;
  margin-top: 6px;
}

:deep(.el-select-dropdown__item) {
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 2px;
  font-size: 0.9375rem;
  color: $gray-700;
  font-weight: 500;
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);

  &:last-child {
    margin-bottom: 0;
  }

  &.is-hovering {
    background-color: rgba(1, 190, 176, 0.08);
    color: $color-teal-accent;
    transform: translateX(2px);
  }

  &.is-selected {
    background-color: rgba(1, 190, 176, 0.12);
    color: $color-teal-accent;
    font-weight: 600;
    position: relative;

    &::after {
      content: '✓';
      position: absolute;
      right: 12px;
      top: 50%;
      transform: translateY(-50%);
      font-size: 1rem;
      font-weight: 700;
    }
  }

  &.is-disabled {
    color: $gray-300;
    cursor: not-allowed;
    
    &:hover {
      background-color: transparent;
      transform: none;
    }
  }
}

// Page Background - Match Activity page
:deep(.app-main-content-layout) {
  background-color: $gray-50;
}

// Title Styling - Match Activity page
:deep(h1) {
  font-size: 1.875rem;
  font-weight: 600;
  color: $gray-900;
  line-height: 1.2;
}

// Responsive Design
@media (max-width: 768px) {
  .c-user-edit-form {
    padding: 1.5rem;
  }

  .c-content-section {
    padding: 1.25rem;
    border-radius: 12px;
  }

  :deep(.grid-2-cols) {
    gap: 1rem;
  }

  :deep(.el-form-item) {
    margin-bottom: 1.25rem;

    .el-form-item__label {
      font-size: 0.8125rem;
    }

    .el-input__inner,
    .el-textarea__inner {
      font-size: 0.8125rem;
    }
  }

  :deep(.el-button) {
    padding: 8px 16px;
    font-size: 0.8125rem;
  }

  .link-input-group {
    flex-direction: column;
    align-items: stretch;

    .el-input {
      width: 100%;
    }

    .el-button {
      width: 100%;
    }
  }
}

@media (max-width: 576px) {
  .c-user-edit-form {
    padding: 1rem;
  }

  .c-content-section {
    padding: 1rem;
    border-radius: 12px;
  }
}
</style>

<script>

import {
  defineComponent,
  ref,
  reactive,
  onMounted,
  computed,
} from 'vue';
import { mask } from 'vue-the-mask';
import VueMultiselect from 'vue-multiselect';
import QrcodeVue from 'qrcode.vue';
import { ElMessage } from 'element-plus';
// import { bullhorns, orgValues, channels } from '@/data/onboarding-static';
import shared from './shared.ts';
import UserCommonSections from './UserCommonSections.vue';

const itemData = {
  id: '',
  firstname: 'User Name',
  lastname: 'User Name',
  slug: '',
  bio: '',
  why: '',
  active: true,
  bullhorns: [],
  channels: [],
  values: [],
  interests: [],
  status: '',
  email: 'emailname@provider.com',
  phone: '(123) 456-789',
  age: 0,
  createdAt: '02 / 19 / 2021',
  organizations: [],
};

export default defineComponent({
  name: 'ManageContent_UserEdit',
  components: { UserCommonSections, VueMultiselect, QrcodeVue },
  directives: { mask },

  setup() {
    const {
      route,
      router,
      loading,
      getUserProfile,
      dateAndTime,
      updateUserProfileByAdmin,
      removeItemGetConfirm,
      removeUser,
    } = shared();
    // Get userId from route params when needed
    const item = reactive(itemData);
    const accountStatus = reactive({
      items: [
        {
          type: 'Active',
          value: true,
        },
        {
          type: 'Archived',
          value: false,
        },
      ],
    });

    // Computed property for user profile URL
    const userUrl = computed(() => {
      if (item.slug) {
        return `${import.meta.env.VITE_WEB_BASE_URL}/web/user/${item.slug}`;
      }
      // Fallback to ID-based URL when no slug is available
      const currentUserId = item.id || route.params.userId;
      return `${import.meta.env.VITE_WEB_BASE_URL}/web/profile/user/${currentUserId}`;
    });

    // Computed property for user deep link (ID-based for CMS)
    const userDeepLink = computed(() => {
      const currentUserId = item.id || route.params.userId;
      return `/profile/user/${currentUserId}`;
    });

    onMounted(async () => {
      const response = await getUserProfile(route.params.userId);
      const { user } = response;
      item.firstname = user.firstname;
      item.lastname = user.lastname;
      item.slug = user.slug;
      item.phone = user.phone;
      item.email = user.email;
      item.bio = response.bio;
      item.why = response.why;
      item.avatar = response.avatar;
      item.active = { type: user.active ? 'Active' : 'Archived', value: user.active };
      item.age = user.age;
      item.is_creator = user.is_creator;
      item.bullhorns = response.bullhorns;
      item.channels = response.channels;
      item.values = response.values;
      item.interests = response.interests;
      [item.createdAt] = dateAndTime(user.createdAt);
      item.organizations = response.organizations;
    });
    const refForm = ref(null);

    const slugTip = computed(() => 'A slug is a URL-friendly version of your user name. It creates clean, memorable URLs like "spotlight.xyz/web/user/your-name". '
      + 'Slugs improve SEO, make sharing easier, and provide a professional appearance. Use lowercase letters, numbers, and hyphens only.');

    function sanitizeSlug() {
      item.slug = item.slug.replace(/[^a-z0-9-]/g, '').toLowerCase();
    }

    function validateForm() {
      loading.value = true;
      refForm.value.validate(async (valid) => {
        if (valid) {
          try {
            await updateUserProfileByAdmin({
              userId: route.params.userId,
              firstname: item.firstname,
              lastname: item.lastname,
              slug: item.slug,
              bio: item.bio,
              why: item.why,
              active: item.active.value,
              is_creator: item.is_creator,
            });
            ElMessage({
              message: 'User profile updated successfully',
              type: 'success',
            });
          } catch (error) {
            console.error('Error updating user profile:', error);

            // Extract error message from GraphQL response
            let errorMessage = 'Failed to update user profile';
            if (error.graphQLErrors && error.graphQLErrors.length > 0) {
              errorMessage = error.graphQLErrors[0].message;
            } else if (error.message) {
              errorMessage = error.message;
            }

            ElMessage({
              message: errorMessage,
              type: 'error',
            });
          } finally {
            loading.value = false;
          }
        } else {
          loading.value = false;
        }
        return false;
      });
    }

    function removeItem() {
      removeItemGetConfirm(async () => {
        await removeUser(route.params.userId);
        router.push('/manage/users');
      });
    }

    function viewOrganization(id, event) {
      event.preventDefault();
      router.push(`/manage/orgs/${id}/edit`);
    }
    async function copyUserDeepLink() {
      try {
        await navigator.clipboard.writeText(userDeepLink.value);
        ElMessage({
          message: 'User deep link copied to clipboard!',
          type: 'success',
        });
      } catch (error) {
        console.error('Failed to copy user deep link:', error);
        ElMessage({
          message: 'Failed to copy deep link',
          type: 'error',
        });
      }
    }

    async function copyUserUrl() {
      try {
        const urlToCopy = userUrl.value;
        console.log('Copying URL:', urlToCopy);
        
        if (!urlToCopy) {
          throw new Error('URL is empty');
        }
        
        await navigator.clipboard.writeText(urlToCopy);
        ElMessage({
          message: 'User profile URL copied to clipboard!',
          type: 'success',
        });
      } catch (error) {
        console.error('Failed to copy user URL:', error);
        
        // Fallback method for older browsers
        try {
          const textArea = document.createElement('textarea');
          textArea.value = userUrl.value;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          
          ElMessage({
            message: 'User profile URL copied to clipboard!',
            type: 'success',
          });
        } catch (fallbackError) {
          console.error('Fallback copy also failed:', fallbackError);
          ElMessage({
            message: 'Failed to copy URL. Please copy manually.',
            type: 'error',
          });
        }
      }
    }

    return {
      loading,
      refForm,
      rules: {},
      item,
      ElMessage,
      validateForm,
      bioTip: 'Gush about yourself. Consider sharing your north star, what makes you laugh, and whatever else about the earned trophies in work, life, and cosmos that make up your special sauce',
      myWhyTip: 'Define your "my purpose statement" to capture your catalyst and commitment. Consider including what you want to accomplish, who that will be for, and how you plan to make stuff better',
      viewOrganization,
      removeItem,
      accountStatus,
      slugTip,
      sanitizeSlug,
      userUrl,
      userDeepLink,
      copyUserDeepLink,
      copyUserUrl,
    };
  },
});
</script>
